import React, { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
const loadTurnstile = () => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not start")));
            script.onerror = () => {
                scriptPromise = null; // a later mount may try again
                reject(new Error("Turnstile could not be loaded"));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

// Cloudflare Turnstile challenge. `onToken(token)` fires when it is passed; `onExpire()` when the token times out;
// `onError(message)` when the challenge cannot load or run. Changing `resetKey` asks for a fresh challenge (a token is
// single use, so call it after every submit that fails).
export default function TurnstileWidget({ siteKey, action, resetKey = 0, onToken, onExpire, onError }) {
    const container = useRef(null);
    const widgetId = useRef(null);
    const handlers = useRef({});
    handlers.current = { onToken, onExpire, onError };

    useEffect(() => {
        let cancelled = false;
        loadTurnstile()
            .then(turnstile => {
                if (cancelled || !container.current) return;
                widgetId.current = turnstile.render(container.current, {
                    sitekey: siteKey,
                    action,
                    // The normal widget is 300px wide; use the compact one where the form is narrower.
                    size: container.current.clientWidth < 300 ? "compact" : "normal",
                    theme: "auto",
                    language: "auto",
                    retry: "auto",
                    "refresh-expired": "manual",
                    "refresh-timeout": "manual",
                    callback: token => handlers.current.onToken && handlers.current.onToken(token),
                    "expired-callback": () => handlers.current.onExpire && handlers.current.onExpire(),
                    "timeout-callback": () => handlers.current.onExpire && handlers.current.onExpire(),
                    "error-callback": () => handlers.current.onError && handlers.current.onError("The security check could not run. Check your connection and try again.")
                });
            })
            .catch(() => handlers.current.onError && handlers.current.onError("The security check could not be loaded. Check your connection and reload the page."));
        return () => {
            cancelled = true;
            if (widgetId.current !== null && window.turnstile) {
                try {
                    window.turnstile.remove(widgetId.current);
                } catch (e) {
                    // already gone
                }
            }
            widgetId.current = null;
        };
    }, [siteKey, action]);

    useEffect(() => {
        if (resetKey > 0 && widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
    }, [resetKey]);

    return <div ref={container} className="d-flex justify-content-center" role="group" aria-label="Security check" data-testid="turnstile-widget" />;
}
